import {
  DEFAULT_ACTIVITY_TIMEZONE,
  addCalendarDaysYmd,
  formatIsoWithLocalOffset,
  formatWallClockYmd,
  instantToHourFraction,
  dayIndexInWallClockViewport,
  localDateTimeFromParts,
  wallClockInTimeZone,
} from '../../utils/activityDateTime';
import { categoryToTradeKey } from '../../utils/categoryToTrade';
import type {
  AbsenceBlock,
  AbsenceKind,
  BoardActivity,
  BoardEmployee,
  GridBlock,
  PlantafelCreateDraft,
  PlantafelViewMode,
  UtilizationDay,
} from './plantafelTypes';
import { PENDING_CREATE_BLOCK_ID } from './plantafelTypes';
import { HOUR_END, HOUR_START } from './plantafelTypes';
import type { PlantafelDayHours, PlantafelVisibleWeekdays } from '../../utils/plantafelSettings';
import {
  clipBlockHoursToViewport,
  DEFAULT_PLANTAFEL_DISPLAY_SETTINGS,
  hourFractionInViewport,
  plantafelHourSpan,
} from '../../utils/plantafelSettings';
import {
  absoluteToVisibleColumn,
  clipSpanToVisibleColumns,
  isAbsoluteDayVisible,
  visibleColumnCount,
  weekDayWidthPct,
} from '../../utils/plantafelVisibleDays';
import { addDays, localDateKey, startOfDay } from './plantafelViewport';

export { getWeekStartMonday } from './plantafelViewport';

function isWallClockMidnight(instant: Date, timeZone: string): boolean {
  const w = wallClockInTimeZone(instant, timeZone);
  return w.hh === 0 && w.mi === 0 && w.se === 0;
}

function daySpanInViewport(
  start: Date,
  end: Date,
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  visibleWeekdays?: PlantafelVisibleWeekdays,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): { dayStart: number; dayEnd: number } | null {
  // Tag columns are labeled by browser localDateKey(viewportStart). Match activities
  // whose company calendar day equals that column key — do not use company-YMD of the
  // browser-midnight Instant (can be the previous/next company day).
  if (viewMode === 'tag') {
    const columnYmd = localDateKey(viewportStart);
    const startYmd = formatWallClockYmd(start, timeZone);
    const endAdj = end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : end;
    const endYmd = formatWallClockYmd(endAdj, timeZone);
    if (endYmd < columnYmd || startYmd > columnYmd) return null;
    return { dayStart: 0, dayEnd: 0 };
  }

  const viewportDayCount = viewMode === 'monat' ? (monthDayCount ?? 35) : 7;
  const viewportEndExclusive = addDays(viewportStart, viewportDayCount);
  if (end <= viewportStart || start >= viewportEndExclusive) return null;

  const clipStart = start < viewportStart ? viewportStart : start;
  const clipEnd = end > viewportEndExclusive ? viewportEndExclusive : end;
  const clipEndForIndex =
    clipEnd.getTime() > clipStart.getTime() ? new Date(clipEnd.getTime() - 1) : clipEnd;
  const sIdx = dayIndexInWallClockViewport(
    viewportStart,
    clipStart,
    viewMode,
    timeZone,
    monthDayCount,
  );
  const eIdx = dayIndexInWallClockViewport(
    viewportStart,
    clipEndForIndex,
    viewMode,
    timeZone,
    monthDayCount,
  );
  if (sIdx < 0 || eIdx < 0) return null;
  const maxDay = viewMode === 'monat' ? (monthDayCount ?? 35) - 1 : 6;
  const dayStart = Math.max(0, sIdx < 0 ? 0 : sIdx);
  const dayEnd = Math.min(maxDay, eIdx < 0 ? maxDay : eIdx);
  if (dayStart > dayEnd) return null;
  if (visibleWeekdays?.length) {
    const hasVisible = clipSpanToVisibleColumns(dayStart, dayEnd, visibleWeekdays);
    if (!hasVisible) return null;
  }
  return { dayStart, dayEnd };
}

export function activityToGridBlock(
  activity: BoardActivity,
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  visibleWeekdays?: PlantafelVisibleWeekdays,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): GridBlock | null {
  if (!activity.due_at) return null;
  const start = new Date(activity.due_at);
  const end = activity.end_at ? new Date(activity.end_at) : new Date(start.getTime() + 60 * 60 * 1000);
  const span = daySpanInViewport(
    start,
    end,
    viewportStart,
    viewMode,
    monthDayCount,
    visibleWeekdays,
    timeZone,
  );
  if (!span) return null;

  let clipStart: Date;
  let clipEnd: Date;
  if (viewMode === 'tag') {
    const columnYmd = localDateKey(viewportStart);
    const dayStartBound = localDateTimeFromParts(columnYmd, 0, timeZone);
    const dayEndExclusive = localDateTimeFromParts(addCalendarDaysYmd(columnYmd, 1), 0, timeZone);
    clipStart = start < dayStartBound ? dayStartBound : start;
    clipEnd = end > dayEndExclusive ? dayEndExclusive : end;
  } else {
    const viewportDayCount = viewMode === 'monat' ? (monthDayCount ?? 35) : 7;
    const viewportEndExclusive = addDays(viewportStart, viewportDayCount);
    clipStart = start < viewportStart ? viewportStart : start;
    clipEnd = end > viewportEndExclusive ? viewportEndExclusive : end;
  }

  let hourStart = instantToHourFraction(clipStart, timeZone);
  let hourEnd =
    clipEnd.getTime() > clipStart.getTime() && isWallClockMidnight(clipEnd, timeZone)
      ? 24
      : instantToHourFraction(clipEnd, timeZone);
  if (hourEnd <= hourStart) hourEnd = hourStart + 0.5;
  hourStart = Math.max(0, Math.min(24, hourStart));
  hourEnd = Math.max(0, Math.min(24, hourEnd));
  const customer = String(activity.lead_name ?? '').trim() || '—';
  return {
    id: activity.id,
    activityId: activity.id,
    leadId: activity.lead_id,
    title: activity.title,
    customer,
    trade: categoryToTradeKey(activity.lead_category),
    activityType: String(activity.activity_type ?? 'task'),
    status: activity.scheduling_status,
    priority: activity.priority,
    dayStart: span.dayStart,
    dayEnd: span.dayEnd,
    hourStart,
    hourEnd,
    scheduleDueAt: activity.due_at,
    scheduleEndAt: activity.end_at ?? formatIsoWithLocalOffset(end, timeZone),
    assigneeEmployeeIds: activity.assignee_employees.map((e) => e.id),
    primaryEmployeeId:
      activity.primary_employee_id ||
      activity.assignee_employees[0]?.id ||
      null,
    orderNo: activity.lead_display_id,
    isRecurring: Boolean(activity.series_id),
  };
}

/** Vorschau-Balken aus Drawer-Entwurf (bleibt bis Speichern sichtbar). */
export function createDraftToGridBlock(
  draft: PlantafelCreateDraft,
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  title = 'Neuer Termin',
  visibleWeekdays?: PlantafelVisibleWeekdays,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): GridBlock | null {
  const pseudo: BoardActivity = {
    id: PENDING_CREATE_BLOCK_ID,
    lead_id: draft.lead_id ?? null,
    title,
    activity_type: 'task',
    due_at: draft.due_at,
    end_at: draft.end_at,
    priority: null,
    notes: null,
    checklist_id: null,
    done: false,
    scheduling_status: 'bestaetigt',
    primary_employee_id: draft.assignee_employee_ids?.[0] ?? null,
    lead_display_id: null,
    lead_name: null,
    lead_title: null,
    lead_category: null,
    activity_site_address: null,
    lead_site_address: null,
    lead_site_latitude: null,
    lead_site_longitude: null,
    lead_phone: null,
    assignee_users: [],
    assignee_employees: (draft.assignee_employee_ids ?? []).map((id) => ({
      id,
      first_name: '',
      last_name: '',
      color_idx: 0,
      position: '',
    })),
  };
  const block = activityToGridBlock(
    pseudo,
    viewportStart,
    viewMode,
    monthDayCount,
    visibleWeekdays,
    timeZone,
  );
  if (!block) return null;
  return {
    ...block,
    id: PENDING_CREATE_BLOCK_ID,
    activityId: '',
    isPendingCreate: true,
  };
}

const ABSENCE_TITLES: Record<AbsenceKind, string> = {
  urlaub: 'Urlaub',
  krank: 'Krank (AU)',
  feiertag: 'Feiertag',
  freischicht: 'Freischicht',
  stundenabbau: 'Freizeitausgleich',
  fehlzeit: 'Unbezahlte Fehlzeit',
  sonderurlaub: 'Sonderurlaub',
  krankengeld: 'Krankengeld',
};

/** Abwesenheiten ohne geplante Stunden in der Plantafel-Auslastung. */
const ZERO_HOUR_ABSENCE_KINDS = new Set<AbsenceKind>(['fehlzeit', 'krankengeld']);

function absenceIsFullDay(block: GridBlock): boolean {
  if (!block.isAbsence) return false;
  if (block.isAbsence === 'freischicht') {
    return block.hourStart === HOUR_START && block.hourEnd === HOUR_END;
  }
  return true;
}

/** Führt benachbarte ganztägige Abwesenheits-Blöcke pro Mitarbeiter zusammen (Titel nur einmal). */
export function mergeAdjacentAbsenceBlocks(blocks: GridBlock[]): GridBlock[] {
  const activities = blocks.filter((b) => !b.isAbsence);
  const absences = blocks.filter((b) => b.isAbsence && absenceIsFullDay(b));
  const timedAbsences = blocks.filter((b) => b.isAbsence && !absenceIsFullDay(b));
  if (absences.length === 0) return [...activities, ...timedAbsences];

  const groups = new Map<string, GridBlock[]>();
  for (const b of absences) {
    const key = `${b.primaryEmployeeId ?? ''}:${b.isAbsence}`;
    const list = groups.get(key) ?? [];
    list.push(b);
    groups.set(key, list);
  }

  const merged: GridBlock[] = [];
  for (const list of groups.values()) {
    const sorted = [...list].sort((a, b) => a.dayStart - b.dayStart);
    let current = { ...sorted[0] };
    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      const adjacent =
        current.title === next.title &&
        current.isAbsence === next.isAbsence &&
        current.primaryEmployeeId === next.primaryEmployeeId &&
        current.dayEnd + 1 === next.dayStart;
      if (adjacent) {
        current = {
          ...current,
          dayEnd: next.dayEnd,
          scheduleEndAt: next.scheduleEndAt ?? current.scheduleEndAt,
          id: `abs-merge-${current.primaryEmployeeId}-${current.isAbsence}-${current.dayStart}-${next.dayEnd}`,
        };
      } else {
        merged.push(current);
        current = { ...next };
      }
    }
    merged.push(current);
  }

  return [...activities, ...merged, ...timedAbsences];
}

export function absenceToGridBlock(
  absence: AbsenceBlock,
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  visibleWeekdays?: PlantafelVisibleWeekdays,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): GridBlock | null {
  const dayKey = String(absence.entry_date).slice(0, 10);
  const d = localDateTimeFromParts(dayKey, 12, timeZone);
  const dayIdx = dayIndexInWallClockViewport(viewportStart, d, viewMode, timeZone, monthDayCount);
  if (dayIdx < 0) return null;
  if (visibleWeekdays?.length && !isAbsoluteDayVisible(dayIdx, visibleWeekdays)) return null;
  const kind = absence.absence_kind;
  if (kind === 'other') return null;

  const title =
    kind === 'feiertag' && absence.holiday_name?.trim()
      ? absence.holiday_name.trim()
      : ABSENCE_TITLES[kind];

  const scheduleDueAt = localWallTimeToIsoForAbsenceStart(dayKey, timeZone);
  const scheduleEndAt = localWallTimeToIsoForAbsenceEnd(dayKey, timeZone);

  return {
    id: `abs-${absence.id}`,
    activityId: '',
    leadId: null,
    title,
    customer: '—',
    trade: 'wartung',
    activityType: 'task',
    status: 'bestaetigt',
    priority: null,
    dayStart: dayIdx,
    dayEnd: dayIdx,
    hourStart: HOUR_START,
    hourEnd: HOUR_END,
    scheduleDueAt,
    scheduleEndAt,
    assigneeEmployeeIds: [absence.employee_id],
    primaryEmployeeId: absence.employee_id,
    orderNo: null,
    isAbsence: kind,
  };
}

function localWallTimeToIsoForAbsenceStart(dayKey: string, timeZone: string): string {
  return formatIsoWithLocalOffset(localDateTimeFromParts(dayKey, 0, timeZone), timeZone);
}

function localWallTimeToIsoForAbsenceEnd(dayKey: string, timeZone: string): string {
  // 23:59 wall clock
  const totalMins = 23 * 60 + 59;
  return formatIsoWithLocalOffset(
    localDateTimeFromParts(dayKey, totalMins / 60, timeZone),
    timeZone,
  );
}

function isWeekdayInBlock(dayIndex: number): boolean {
  return dayIndex % 7 < 5;
}

function isMultiDayActivityBlock(block: GridBlock): boolean {
  return !block.isAbsence && block.dayStart !== block.dayEnd;
}

function multiDayActivityWeekdayHours(block: GridBlock, expectedHoursPerDay: number): number {
  let weekdays = 0;
  for (let d = block.dayStart; d <= block.dayEnd; d++) {
    if (isWeekdayInBlock(d)) weekdays++;
  }
  return weekdays * expectedHoursPerDay;
}

/** Stunden eines Blocks an einem Viewport-Tag (für Tages-Auslastung). */
export function gridBlockHoursOnDay(
  block: GridBlock,
  dayIdx: number,
  expectedHoursPerDay: number,
): number {
  if (dayIdx < block.dayStart || dayIdx > block.dayEnd) return 0;
  if (block.isAbsence) {
    if (ZERO_HOUR_ABSENCE_KINDS.has(block.isAbsence)) return 0;
    return isWeekdayInBlock(dayIdx) ? expectedHoursPerDay : 0;
  }
  if (!isWeekdayInBlock(dayIdx)) return 0;
  if (isMultiDayActivityBlock(block)) return expectedHoursPerDay;
  return Math.max(0, block.hourEnd - block.hourStart);
}

export function gridBlockAssignedToEmployee(block: GridBlock, empId: string): boolean {
  return block.primaryEmployeeId === empId || block.assigneeEmployeeIds.includes(empId);
}

/** Stunden für die Zeilen-Summe links (Abwesenheit: Sollstunden je Werktag oder AZK-Stunden). */
export function gridBlockScheduledHours(
  block: GridBlock,
  expectedHoursPerDay: number,
): number {
  if (block.isAbsence) {
    if (ZERO_HOUR_ABSENCE_KINDS.has(block.isAbsence)) return 0;
    return multiDayActivityWeekdayHours(block, expectedHoursPerDay);
  }
  if (isMultiDayActivityBlock(block)) {
    return multiDayActivityWeekdayHours(block, expectedHoursPerDay);
  }
  return Math.max(0, block.hourEnd - block.hourStart);
}

/** Auslastung pro Tag — gleiche Block-/Stundenlogik wie in den Mitarbeiterzeilen. */
export function buildUtilizationDays(
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  employees: BoardEmployee[],
  blocks: GridBlock[],
  monthDayCount?: number,
): UtilizationDay[] {
  const count = viewMode === 'tag' ? 1 : viewMode === 'woche' ? 7 : (monthDayCount ?? 35);
  const days: UtilizationDay[] = [];
  for (let i = 0; i < count; i++) {
    const d = addDays(viewportStart, i);
    const dayKey = localDateKey(d);
    const wknd = d.getDay() === 0 || d.getDay() === 6;
    let capacity = 0;
    let scheduled = 0;
    if (!wknd) {
      for (const emp of employees) {
        capacity += emp.expected_hours_per_day;
        for (const b of blocks) {
          if (!gridBlockAssignedToEmployee(b, emp.id)) continue;
          scheduled += gridBlockHoursOnDay(b, i, emp.expected_hours_per_day);
        }
      }
    }
    const scheduled_hours = Math.round(scheduled * 10) / 10;
    const capacity_hours = Math.round(capacity * 10) / 10;
    days.push({
      day: dayKey,
      scheduled_hours,
      capacity_hours,
      pct: capacity_hours > 0 ? scheduled_hours / capacity_hours : 0,
    });
  }
  return days;
}

export function getViewSpan(
  block: GridBlock,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  visibleWeekdays?: PlantafelVisibleWeekdays,
  dayHours: PlantafelDayHours = DEFAULT_PLANTAFEL_DISPLAY_SETTINGS.dayHours,
): { leftPct: number; widthPct: number } | null {
  const isAllDay = Boolean(block.isAbsence && absenceIsFullDay(block));
  const visible = visibleWeekdays?.length ? visibleWeekdays : [0, 1, 2, 3, 4, 5, 6];
  const hourSpan = plantafelHourSpan(dayHours);

  if (viewMode === 'tag') {
    if (block.dayStart !== 0 || block.dayEnd !== 0) return null;
    if (isAllDay) {
      return { leftPct: 0, widthPct: 100 };
    }
    const { visStart, visEnd } = clipBlockHoursToViewport(
      block.hourStart,
      block.hourEnd,
      dayHours,
    );
    return {
      leftPct: ((visStart - dayHours.hourStart) / hourSpan) * 100,
      widthPct: Math.max(((visEnd - visStart) / hourSpan) * 100, 4),
    };
  }

  if (viewMode === 'monat') {
    const weekCount = Math.max(1, Math.ceil((monthDayCount ?? 35) / 7));
    const cols = visibleColumnCount(visible, weekCount);
    const span = clipSpanToVisibleColumns(block.dayStart, block.dayEnd, visible);
    if (!span) return null;
    const dayWidth = 100 / cols;
    const leftPct = span.colStart * dayWidth;
    const rightPct = (span.colEnd + 1) * dayWidth;
    return { leftPct, widthPct: Math.max(rightPct - leftPct, dayWidth * 0.35) };
  }

  const span = clipSpanToVisibleColumns(block.dayStart, block.dayEnd, visible);
  if (!span) return null;
  let hs: number;
  let he: number;
  if (isAllDay) {
    hs = dayHours.hourStart;
    he = dayHours.hourEnd;
  } else if (span.colEnd > span.colStart) {
    // Multi-day block: hourStart belongs to the start day (colStart) and
    // hourEnd to the end day (colEnd), so clip each to the viewport
    // independently instead of treating them as one same-day interval.
    hs = Math.min(Math.max(block.hourStart, dayHours.hourStart), dayHours.hourEnd);
    he = Math.min(Math.max(block.hourEnd, dayHours.hourStart), dayHours.hourEnd);
  } else {
    ({ visStart: hs, visEnd: he } = clipBlockHoursToViewport(
      block.hourStart,
      block.hourEnd,
      dayHours,
    ));
  }
  const dayWidth = weekDayWidthPct(visible);
  const leftPct =
    span.colStart * dayWidth + ((hs - dayHours.hourStart) / hourSpan) * dayWidth;
  const rightPct =
    span.colEnd * dayWidth + ((he - dayHours.hourStart) / hourSpan) * dayWidth;
  return { leftPct, widthPct: Math.max(rightPct - leftPct, dayWidth * 0.35) };
}

export function getTodayLeftPct(
  viewportStart: Date,
  now: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  visibleWeekdays?: PlantafelVisibleWeekdays,
  dayHours: PlantafelDayHours = DEFAULT_PLANTAFEL_DISPLAY_SETTINGS.dayHours,
  timeZone: string = DEFAULT_ACTIVITY_TIMEZONE,
): number {
  const hour = instantToHourFraction(now, timeZone);
  const nowPct = hourFractionInViewport(hour, dayHours);
  const visible = visibleWeekdays?.length ? visibleWeekdays : [0, 1, 2, 3, 4, 5, 6];

  if (viewMode === 'tag') {
    if (localDateKey(viewportStart) !== formatWallClockYmd(now, timeZone)) return -1;
    return nowPct * 100;
  }

  const dayIdx = dayIndexInWallClockViewport(
    viewportStart,
    now,
    viewMode,
    timeZone,
    monthDayCount,
  );
  if (dayIdx < 0) return -1;

  const col = absoluteToVisibleColumn(dayIdx, visible);
  if (col == null) return -1;

  if (viewMode === 'monat') {
    const weekCount = Math.max(1, Math.ceil((monthDayCount ?? 35) / 7));
    const cols = visibleColumnCount(visible, weekCount);
    return ((col + nowPct) / cols) * 100;
  }
  return ((col + nowPct) / visible.length) * 100;
}
